import React, { useState, useEffect } from 'react';

interface CurrencyInputProps {
  label: string;
  value: string | number | null;
  onChange: (value: string) => void;
  id: string;
  name: string;
  required?: boolean;
  error?: string;
  className?: string;
  placeholder?: string;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  label,
  value,
  onChange,
  id,
  name,
  required = false,
  error,
  className = '',
  placeholder = '0.00'
}) => {
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (value !== null && value !== '') {
      setDisplayValue(value.toString());
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    onChange(inputValue);
  };

  const formatCurrency = (value: string | number | null): string => {
    if (!value || value === '') return '';
    
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numericValue)) return '';
    
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numericValue);
  };

  const numericValue = displayValue ? parseFloat(displayValue) : null;
  const formattedValue = formatCurrency(numericValue);

  return (
    <div className={`mb-4 ${className}`}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="number"
          id={id}
          name={name}
          value={displayValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          required={required}
          step="0.01"
          min="0"
          className={`w-full h-12 pr-10 pl-3 border ${
            error ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
          } rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm bg-white text-gray-900`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">$</span>
        </div>
      </div>
      
      {/* Formatted value display below input */}
      {formattedValue && (
        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {formattedValue}
        </div>
      )}
      
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default CurrencyInput;
